// src/components/StaffPayrollInputsPage.js
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../services/api";
import "./RunNewPage.css"; // reuse same styles as RunNewPay

const StaffPayrollInputsPage = () => {
  const { id: businessId } = useParams();

  // mirrors RunNewPay state
  const [hourlyEmployees, setHourlyEmployees] = useState([]);
  const [salariedEmployees, setSalariedEmployees] = useState([]);
  const [payPeriod, setPayPeriod] = useState(null);

  const [employeeInputs, setEmployeeInputs] = useState({});
  const [expandedRows, setExpandedRows] = useState({});
  const [excludedEmployees, setExcludedEmployees] = useState(new Set());

  const [leaveRequests, setLeaveRequests] = useState([]);
  const [leaveRequestInputs, setLeaveRequestInputs] = useState({});
  const [hourlyVacationHours, setHourlyVacationHours] = useState({});
  const [salariedVacationHours, setSalariedVacationHours] = useState({});

  // which employees already have a payroll_inputs record this period
  const [hasRecordByEmp, setHasRecordByEmp] = useState({});
  // per-employee "edit" toggle (for employees that have a record)
  const [editing, setEditing] = useState({}); // { [employeeId]: true|false }

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // expand/collapse all
const [allExpanded, setAllExpanded] = useState(false);


  const frequencyMultiplier = {
    weekly: 52,
    biweekly: 26,
    semimonthly: 24,
    monthly: 12,
  };




  useEffect(() => {
  let cancelled = false;

  const toISO = (d) => {
    if (!d) return "";
    // if it's already "YYYY-MM-DD", keep it
    if (typeof d === "string" && /^\d{4}-\d{2}-\d{2}/.test(d)) return d.slice(0, 10);
    const dt = new Date(d);
    return Number.isNaN(dt.getTime()) ? "" : dt.toISOString().slice(0, 10);
  };

  (async () => {
    setLoading(true);

    try {
      // 1) Pay period (required)
      const ppResp = await api.get(`/staff/payroll/next/${businessId}`);
      if (cancelled) return;
      const pp = ppResp.data;
      setPayPeriod(pp);

      // 2) Employees (sequential; don’t fail the whole page if one call errors)
      let hourly = [];
      let salaried = [];

      try {
        const r = await api.get(`/staff/businesses/${businessId}/employees/hourly`);
        hourly = (r.data || []).map((e) => ({ ...e, type: "hourly" }));
      } catch (e) {
        console.error("Hourly employees failed:", e?.response?.status, e);
      }

      try {
        const r = await api.get(`/staff/businesses/${businessId}/employees/salary`);
        salaried = (r.data || []).map((e) => ({ ...e, type: "salaried" }));
      } catch (e) {
        console.error("Salaried employees failed:", e?.response?.status, e);
      }

      if (cancelled) return;
      setHourlyEmployees(hourly);
      setSalariedEmployees(salaried);

      // 3) Leave requests (best-effort)
      const start = toISO(pp.start_date);
      const end   = toISO(pp.end_date);

      let lr = [];
      try {
        const lrResp = await api.get(`/leave-requests/${businessId}/${start}/${end}`);
        lr = lrResp.data || [];
      } catch (e) {
        console.error("Leave requests failed:", e?.response?.status, e);
      }
      if (cancelled) return;
      setLeaveRequests(lr);

      // 4) Prefill (best-effort)
      let prefill = {};
      try {
        const pf = await api.get(`/staff/payroll/inputs/${businessId}`, { params: { start, end } });
        prefill = pf.data || {};
      } catch (e) {
        console.error("Prefill failed:", e?.response?.status, e);
      }

      if (!cancelled) {
        applyPrefill(prefill, hourly, salaried, pp, lr);
        setError(null);
      }
    } catch (e) {
      console.error("Pay period load failed:", e?.response?.status, e);
      if (!cancelled) setError("Failed to load data. Please try again.");
    } finally {
      if (!cancelled) setLoading(false);
    }
  })();

  return () => { cancelled = true; };
}, [businessId]);




  const getPayPeriodSalary = (annualSalary) => {
    if (!payPeriod || !payPeriod.frequency) return annualSalary ?? 0;
    const mult = frequencyMultiplier[payPeriod.frequency.toLowerCase()] || 1;
    return Number((Number(annualSalary || 0) / mult).toFixed(2));
  };

  const formatDate = (d) => new Date(d).toISOString().split("T")[0];

  const gt0 = (v) => v != null && Number(v) > 0;
const chipClass = (v) => `chip${gt0(v) ? " chip--active" : ""}`;



  const toggleInputs = (employeeId) => {
    setExpandedRows((prev) => ({ ...prev, [employeeId]: !prev[employeeId] }));
  };

  const toggleEdit = (id) =>
    setEditing((prev) => ({ ...prev, [id]: !prev[id] }));

  const fmt = (v) => (v === 0 ? "0" : v == null ? "—" : String(v));

  const handleCheckboxChange = (employeeId) => {
    setExcludedEmployees((prev) => {
      const next = new Set(prev);
      next.has(employeeId) ? next.delete(employeeId) : next.add(employeeId);
      return next;
    });
  };

  const handleInputChange = (employeeId, field, value) => {
    setEmployeeInputs((prev) => ({
      ...prev,
      [employeeId]: {
        ...prev[employeeId],
        [field]:
          value === "" || value === null || value === undefined
            ? null
            : parseFloat(value),
      },
    }));
  };

  const handleLeaveInputChange = (leaveId, employeeId, value) => {
    setLeaveRequestInputs((prevInputs) => {
      const parsed =
        value === "" || value === null || value === undefined
          ? 0
          : parseFloat(value);
      const newInputs = { ...prevInputs, [leaveId]: isNaN(parsed) ? 0 : parsed };

      // sum only this employee's leave rows
      const totalVacationHours = leaveRequests
        .filter((l) => l.employee_id === employeeId)
        .reduce((sum, l) => sum + (newInputs[l.id] ?? 0), 0);

      const emp =
        [...hourlyEmployees, ...salariedEmployees].find((e) => e.id === employeeId) ||
        null;

      if (emp) {
        if (emp.type === "hourly") {
          setHourlyVacationHours((prev) => ({
            ...prev,
            [employeeId]: totalVacationHours,
          }));
          handleInputChange(employeeId, "vacationHours", totalVacationHours);
        } else {
          setSalariedVacationHours((prev) => ({
            ...prev,
            [employeeId]: totalVacationHours,
          }));
          handleInputChange(employeeId, "vacationPay", totalVacationHours);
        }
      }
      return newInputs;
    });
  };

  // prefill overlay from HR inputs table
  const applyPrefill = (prefill, hourly, salaried, _pp, lr) => {
    // 0) which employees already have a saved record?
    const hasMap = {};
    Object.keys(prefill?.inputsByEmployee || {}).forEach((empId) => {
      hasMap[Number(empId)] = true;
    });
    setHasRecordByEmp(hasMap);

    // 1) inputs
    const merged = {};
    [...hourly, ...salaried].forEach((emp) => {
      const v = prefill?.inputsByEmployee?.[emp.id] || {};
      merged[emp.id] = {
        regularHours: v.regularHours ?? null,
        overtimeHours: v.overtimeHours ?? null,
        doubletimeHours: v.doubletimeHours ?? null,
        cashAdvance: v.cashAdvance ?? null,
        bonus: v.bonus ?? null,
        tips: v.tips ?? null,
        reimbursement: v.reimbursement ?? null,
        other: v.other ?? null,
        vacationHours: emp.type === "hourly" ? v.vacationHours ?? null : undefined,
        vacationPay: emp.type === "salaried" ? v.vacationPay ?? null : undefined,
      };
    });
    setEmployeeInputs(merged);

    // 2) excluded set
    const excluded = new Set();
    Object.entries(prefill?.inputsByEmployee || {}).forEach(([empId, v]) => {
      if (v?.is_excluded) excluded.add(Number(empId));
    });
    setExcludedEmployees(excluded);

    // 3) leave allocations → per-leave inputs AND rollup hours boxes
    const newLeaveInputs = {};
    const hvh = {};
    const svh = {};

    // index: payroll_inputs_id by employee
    const piByEmp = {};
    Object.entries(prefill?.inputsByEmployee || {}).forEach(([empId, v]) => {
      if (v?.payroll_inputs_id) piByEmp[Number(empId)] = v.payroll_inputs_id;
    });

    lr.forEach((leave) => {
      const pid = piByEmp[leave.employee_id];
      if (!pid) return;
      const arr = prefill?.leaveHoursByInputId?.[pid] || [];
      const match = arr.find((x) => x.leave_request_id === leave.id);
      if (match) newLeaveInputs[leave.id] = Number(match.hours_this_payroll || 0);
    });

    // compute totals per employee based on newLeaveInputs
    [...hourly, ...salaried].forEach((emp) => {
      const sum = lr
        .filter((l) => l.employee_id === emp.id)
        .reduce((acc, l) => acc + (newLeaveInputs[l.id] ?? 0), 0);
      if (emp.type === "hourly") hvh[emp.id] = sum;
      else svh[emp.id] = sum;
    });

    setLeaveRequestInputs(newLeaveInputs);
    setHourlyVacationHours(hvh);
    setSalariedVacationHours(svh);
  };

  

  // helpers (place inside component, above return)
const allEmployees = [...hourlyEmployees, ...salariedEmployees];

const expandAll = () => {
  const map = {};
  allEmployees.forEach(e => { map[e.id] = true; });
  setExpandedRows(map);
  setAllExpanded(true);
};

const collapseAll = () => {
  setExpandedRows({});
  setAllExpanded(false);
};

// keep everything expanded if the employee list changes after load
useEffect(() => {
  if (!allExpanded) return;
  const map = {};
  allEmployees.forEach(e => { map[e.id] = true; });
  setExpandedRows(map);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [hourlyEmployees, salariedEmployees]);


  const handleSave = async () => {
    // enforce: hourly regular hours required unless excluded
    const missing = hourlyEmployees.filter((emp) => {
      if (excludedEmployees.has(emp.id)) return false;
      const rr = employeeInputs[emp.id]?.regularHours;
      return rr === null || rr === undefined;
    });
    if (missing.length) {
      alert(
        `Regular Hours required for: ${missing
          .map((m) => `${m.first_name} ${m.last_name}`)
          .join(", ")}`
      );
      return;
    }

    const rows = [...hourlyEmployees, ...salariedEmployees].map((emp) => {
      const v = employeeInputs[emp.id] || {};
      return {
        employee_id: emp.id,
        is_excluded: excludedEmployees.has(emp.id),
        regularHours: v.regularHours ?? null,
        overtimeHours: v.overtimeHours ?? null,
        doubletimeHours: v.doubletimeHours ?? null,
        cashAdvance: v.cashAdvance ?? null,
        bonus: v.bonus ?? null,
        tips: v.tips ?? null,
        reimbursement: v.reimbursement ?? null,
        other: v.other ?? null,
        vacationHours: emp.type === "hourly" ? v.vacationHours ?? null : null,
        vacationPay: emp.type === "salaried" ? v.vacationPay ?? null : null,
      };
    });

    const leaveRows = leaveRequests
      .filter((l) => leaveRequestInputs[l.id] != null)
      .map((l) => ({
        employee_id: l.employee_id,
        leave_request_id: l.id,
        hours_this_payroll: Number(leaveRequestInputs[l.id] || 0),
      }));

    try {
      await api.post(`/staff/payroll/inputs/${businessId}/upsert`, {
        start_date: formatDate(payPeriod.start_date),
        end_date: formatDate(payPeriod.end_date),
        pay_date: formatDate(payPeriod.pay_date),
        frequency: payPeriod.frequency,
        rows,
        leaveRows,
      });
      alert("Saved HR inputs.");
      // After save, mark these employees as having records so next render can show chips
      const newHas = { ...hasRecordByEmp };
      rows.forEach((r) => {
        if (!excludedEmployees.has(r.employee_id)) newHas[r.employee_id] = true;
      });
      setHasRecordByEmp(newHas);
      // collapse edit toggles after save (optional)
      setEditing({});
    } catch (err) {
      console.error("Save HR inputs error:", err);
      alert("Failed to save HR inputs.");
    }
  };

  if (loading) return <p>Loading employees...</p>;
  if (error) return <p>{error}</p>;

  return (
    <div className="run-new-main">
      <h4 className="run-new-page-head">Payroll (HR Inputs)</h4>

      {payPeriod && (
        <div className="pay-period-payroll-cont">
          <div className="ppl-pay">
            <label className="pay-period-label">Pay Period:</label>{" "}
            <span className="pay-period-content">
              {new Date(payPeriod.start_date).toLocaleDateString()} -{" "}
              {new Date(payPeriod.end_date).toLocaleDateString()}
              <br />
            </span>
          </div>

          <div className="ppl-pay">
            <label className="pay-period-label">Pay Date:</label>{" "}
            <span className="pay-period-content">
              {new Date(payPeriod.pay_date).toLocaleDateString()}
            </span>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '8px 0' }}>
  <button className="expand-all-btn" onClick={allExpanded ? collapseAll : expandAll}>
    {allExpanded ? 'Collapse all' : 'Expand all'}
  </button>
</div>


      <div className="run-new-employee-list">
        {[...hourlyEmployees, ...salariedEmployees].map((employee) => {
          const employeeLeaveRequests = leaveRequests.filter(
            (leave) => leave.employee_id === employee.id
          );
          const hasLeaveRequests = employeeLeaveRequests.length > 0;

          return (
            <div
              key={employee.id}
              className={`run-new-employee-block ${
                hasLeaveRequests ? "employee-leave-highlight" : ""
              }`}
            >
              {/* optional leave summary + requests (same layout) */}
              {hasLeaveRequests && (
                <>
                  <div className="vacation-summary">
                    <div className="summary-div">Vacation Summary:</div>{" "}
                    {employee.total_vacation_hours_earned ? (
                      <>
                        Earned:{" "}
                        <div>{employee.total_vacation_hours_earned ?? 0} hrs</div>{" "}
                        | Used:{" "}
                        <div>{employee.total_vacation_hours_used ?? 0} hrs</div> |{" "}
                        Available:{" "}
                        <div>{employee.available_vacation_hours ?? 0} hrs</div>
                      </>
                    ) : (
                      "No vacation hours logged yet."
                    )}
                  </div>

                  <div className="leave-requests-section">
                    <h4>Leave Request:</h4>
                    <div className="leave-request-header">
                      <span className="span-type">Type</span>
                      <span className="date-span">Dates</span>
                      <span className="days-req">Total Days</span>
                      <span className="hours-req">Hours Req</span>
                      <span className="hours-paid">Hours Paid</span>
                      <span className="days-paid">Days Paid</span>
                      <span className="hours-left">Hours Left</span>
                      <span className="days-left">Days Left</span>
                      <span className="period-days">Days in Period</span>
                      <span className="this-span">Hrs This Payroll</span>
                    </div>

                    {employeeLeaveRequests.map((leave) => {
                      const totalHoursRequested =
                        Number(leave.total_hours_requested) || 0;
                      const totalHoursPaid = Number(leave.total_hours_paid) || 0;
                      const totalDaysRequested =
                        Number(leave.total_days_requested) || 0;
                      const totalDaysPaid = Number(leave.total_days_paid) || 0;
                      const leaveDaysInPeriod =
                        Number(leave.leave_days_in_period) || 0;

                      return (
                        <div key={leave.id} className="leave-request-row">
                          <span className="span-type">{leave.request_type}</span>
                          <div className="date-span">
                            {new Date(leave.start_date).toLocaleDateString()} -{" "}
                            {new Date(leave.end_date).toLocaleDateString()}
                          </div>
                          <span className="days-req">{totalDaysRequested}</span>
                          <span className="hours-req">{totalHoursRequested}</span>
                          <span className="hours-paid">{totalHoursPaid}</span>
                          <span className="days-paid">{totalDaysPaid}</span>
                          <span className="hours-left">
                            {totalHoursRequested - totalHoursPaid}
                          </span>
                          <span className="days-left">
                            {totalDaysRequested - totalDaysPaid}
                          </span>
                          <span className="period-days">{leaveDaysInPeriod}</span>
                          <input
                            type="number"
                            placeholder="Vac. Hours"
                            value={leaveRequestInputs[leave.id] ?? ""}
                            onChange={(e) =>
                              handleLeaveInputChange(
                                leave.id,
                                leave.employee_id,
                                e.target.value
                              )
                            }
                          />
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {/* Row 1: Basic Info */}
              <div className="employee-info-row">
                <div className="skip-it">
                  <label className="skip-label"> Skip </label>
                  <input
                    type="checkbox"
                    className="run-new-checkbox"
                    checked={excludedEmployees.has(employee.id)}
                    onChange={() => handleCheckboxChange(employee.id)}
                  />
                </div>

                <span className="run-new-name">
                  {employee.first_name} {employee.last_name}
                </span>
                <span className="run-new-type">
                  {employee.type === "hourly" ? "Hourly" : "Salary"}
                </span>
                <span className="run-new-pay">
                  {employee.type === "hourly"
                    ? `$${employee.hourly_amt} /hr`
                    : `$${getPayPeriodSalary(employee.salary_amt)} /period`}
                </span>

                {/* ✏️ only if a record exists for this employee */}
                {hasRecordByEmp[employee.id] && (
                  <button
                    className="toggle-arrow"
                    title={editing[employee.id] ? "Close edit" : "Edit"}
                    onClick={() => toggleEdit(employee.id)}
                    style={{ cursor: "pointer", background: "none", border: "none" }}
                  >
                    {editing[employee.id] ? "▲" : "✏️"}
                  </button>
                )}

                {/* expand/collapse the extra section */}
                <span
                  className="toggle-arrow"
                  onClick={() => toggleInputs(employee.id)}
                >
                  {expandedRows[employee.id] ? "▲" : "▼"}
                </span>
              </div>

              {/* Row 2: Core Fields (inputs if no record or editing; else chips) */}
              <div className="employee-inputs-row">
                {(!hasRecordByEmp[employee.id] || editing[employee.id]) ? (
                  employee.type === "hourly" ? (
                    <>
                      <input
                        type="number"
                        placeholder="Reg. Hours"
                        value={employeeInputs[employee.id]?.regularHours ?? ""}
                        onChange={(e) =>
                          handleInputChange(employee.id, "regularHours", e.target.value)
                        }
                      />
                      <input
                        type="number"
                        placeholder="OT Hours"
                        value={employeeInputs[employee.id]?.overtimeHours ?? ""}
                        onChange={(e) =>
                          handleInputChange(employee.id, "overtimeHours", e.target.value)
                        }
                      />
                      <input
                        type="number"
                        placeholder="DT Hours"
                        value={employeeInputs[employee.id]?.doubletimeHours ?? ""}
                        onChange={(e) =>
                          handleInputChange(employee.id, "doubletimeHours", e.target.value)
                        }
                      />
                      <input
                        type="number"
                        placeholder="Vac. Hours"
                        value={
                          hourlyVacationHours[employee.id] ??
                          employeeInputs[employee.id]?.vacationHours ??
                          ""
                        }
                        onChange={(e) => {
                          const value = e.target.value;
                          setHourlyVacationHours((prev) => ({
                            ...prev,
                            [employee.id]:
                              value === "" ? "" : parseFloat(value) || 0,
                          }));
                          handleInputChange(employee.id, "vacationHours", value);
                        }}
                      />
                    </>
                  ) : (
                    <input
                      type="number"
                      placeholder="Vac. Hours"
                      value={
                        salariedVacationHours[employee.id] ??
                        employeeInputs[employee.id]?.vacationPay ??
                        ""
                      }
                      onChange={(e) => {
                        const value = e.target.value;
                        setSalariedVacationHours((prev) => ({
                          ...prev,
                          [employee.id]: value === "" ? "" : parseFloat(value) || 0,
                        }));
                        handleInputChange(employee.id, "vacationPay", value);
                      }}
                    />
                  )
                ) : (
                  // read-only chips
                  employee.type === "hourly" ? (
                    <>
                      <span className={chipClass(employeeInputs[employee.id]?.regularHours)}>
                        Regular Hours: {fmt(employeeInputs[employee.id]?.regularHours)}
                      </span>
                      <span className={chipClass(employeeInputs[employee.id]?.overtimeHours)}>
                        Overtime Hours: {fmt(employeeInputs[employee.id]?.overtimeHours)}
                      </span>
                      <span className={chipClass(employeeInputs[employee.id]?.doubletimeHours)}>
                        Doubletime Hours: {fmt(employeeInputs[employee.id]?.doubletimeHours)}
                      </span>
                      <span className={chipClass(employeeInputs[employee.id]?.vacationHours)}>
                        Vacation Hours: {fmt(employeeInputs[employee.id]?.vacationHours)}
                      </span>
                    </>
                  ) : (
                    <span className={chipClass(employeeInputs[employee.id]?.vacationPay)}>
                      Vacation Hours: {fmt(employeeInputs[employee.id]?.vacationPay)}
                    </span>
                  )
                )}
              </div>

              {/* Row 3: Expanded Inputs (same classes/fields) */}
              {expandedRows[employee.id] && (
                <>
                  <div className="vacation-summary-expanded">
                    <div className="summary-div">Vacation Summary:</div>{" "}
                    {employee.total_vacation_hours_earned ? (
                      <>
                        Earned:{" "}
                        <div>{employee.total_vacation_hours_earned ?? 0} hrs</div> |{" "}
                        Used: <div>{employee.total_vacation_hours_used ?? 0} hrs</div> |{" "}
                        Available:{" "}
                        <div>{employee.available_vacation_hours ?? 0} hrs</div>
                      </>
                    ) : (
                      "No vacation hours logged yet."
                    )}

                  </div>{/* Cash Advance Balance pill */}
<span
  className="ca-balance"
  title={`Paid Out: $${(employee.cash_advance_paid_out_total)} | Recouped: $${(employee.cash_advance_recouped_total)}`}
  style={{
    marginLeft: 'auto',
    padding: '2px 8px',
    borderRadius: '12px',
    fontWeight: 600,
    background: (employee.cash_advance_balance) > 0 ? '#ffe4cc' : '#e7f7e7',
    color: (employee.cash_advance_balance) > 0 ? '#9a3f00' : '#126b3a',
    whiteSpace: 'nowrap'
  }}
>
  Cash Advance Balance: ${(employee.cash_advance_balance)}
</span>


                  <div className="employee-extra-inputs">
                    {(!hasRecordByEmp[employee.id] || editing[employee.id]) ? (
                      <>
                        <input
                          type="number"
                          placeholder="Cash Advance"
                          value={employeeInputs[employee.id]?.cashAdvance ?? ""}
                          onChange={(e) =>
                            handleInputChange(employee.id, "cashAdvance", e.target.value)
                          }
                        />
                        <input
                          type="number"
                          placeholder="Bonus"
                          value={employeeInputs[employee.id]?.bonus ?? ""}
                          onChange={(e) =>
                            handleInputChange(employee.id, "bonus", e.target.value)
                          }
                        />
                        <input
                          type="number"
                          placeholder="Tips"
                          value={employeeInputs[employee.id]?.tips ?? ""}
                          onChange={(e) =>
                            handleInputChange(employee.id, "tips", e.target.value)
                          }
                        />
                        <input
                          type="number"
                          placeholder="Reimbursement"
                          value={employeeInputs[employee.id]?.reimbursement ?? ""}
                          onChange={(e) =>
                            handleInputChange(employee.id, "reimbursement", e.target.value)
                          }
                        />
                        
                        
                      </>
                    ) : (
                      <>
                        <span className={chipClass(employeeInputs[employee.id]?.cashAdvance)}>
                          Cash Advance {fmt(employeeInputs[employee.id]?.cashAdvance)}
                        </span>
                        <span className={chipClass(employeeInputs[employee.id]?.bonus)}>
                          Bonus: {fmt(employeeInputs[employee.id]?.bonus)}
                        </span>
                        <span className={chipClass(employeeInputs[employee.id]?.tips)}>
                          Tips: {fmt(employeeInputs[employee.id]?.tips)}
                        </span>
                        <span className={chipClass(employeeInputs[employee.id]?.reimbursement)}>
                          Reimbursement: {fmt(employeeInputs[employee.id]?.reimbursement)}
                        </span>
                  
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Use the same submit button class for consistent styling */}
      <button className="run-new-submit" onClick={handleSave}>
        Save
      </button>
    </div>
  );
};

export default StaffPayrollInputsPage;




// // StaffPayrollInputsPage.js
// import React, { useEffect, useState } from "react";
// import { useParams } from "react-router-dom";
// import api from "../services/api";
// import "./RunNewPage.css"; // reuse same styles as RunNewPay

// const StaffPayrollInputsPage = () => {
//   const { id: businessId } = useParams();

//   // mirrors RunNewPay state
//   const [hourlyEmployees, setHourlyEmployees] = useState([]);
//   const [salariedEmployees, setSalariedEmployees] = useState([]);
//   const [payPeriod, setPayPeriod] = useState(null);

//   const [employeeInputs, setEmployeeInputs] = useState({});
//   const [expandedRows, setExpandedRows] = useState({});
//   const [excludedEmployees, setExcludedEmployees] = useState(new Set());

//   const [leaveRequests, setLeaveRequests] = useState([]);
//   const [leaveRequestInputs, setLeaveRequestInputs] = useState({});
//   const [hourlyVacationHours, setHourlyVacationHours] = useState({});
//   const [salariedVacationHours, setSalariedVacationHours] = useState({});

//   const [editing, setEditing] = useState({}); // { [employeeId]: true|false }

//   const [hasRecordByEmp, setHasRecordByEmp] = useState({});


//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);

//   const frequencyMultiplier = {
//     weekly: 52,
//     biweekly: 26,
//     semimonthly: 24,
//     monthly: 12,
//   };

//   const getPayPeriodSalary = (annualSalary) => {
//     if (!payPeriod || !payPeriod.frequency) return annualSalary ?? 0;
//     const mult = frequencyMultiplier[payPeriod.frequency.toLowerCase()] || 1;
//     return Number((Number(annualSalary || 0) / mult).toFixed(2));
//   };

//   const formatDate = (d) => new Date(d).toISOString().split("T")[0];

//   // toggle row inputs
//   const toggleInputs = (employeeId) => {
//     setExpandedRows((prev) => ({ ...prev, [employeeId]: !prev[employeeId] }));
//   };

//   const handleCheckboxChange = (employeeId) => {
//     setExcludedEmployees((prev) => {
//       const next = new Set(prev);
//       next.has(employeeId) ? next.delete(employeeId) : next.add(employeeId);
//       return next;
//     });
//   };

//   const handleInputChange = (employeeId, field, value) => {
//     setEmployeeInputs((prev) => ({
//       ...prev,
//       [employeeId]: {
//         ...prev[employeeId],
//         [field]:
//           value === "" || value === null || value === undefined
//             ? null
//             : parseFloat(value),
//       },
//     }));
//   };


//   const toggleEdit = (id) => setEditing(prev => ({ ...prev, [id]: !prev[id] }));
// const fmt = (v) => (v === 0 ? "0" : (v == null ? "—" : String(v)));


//   const handleLeaveInputChange = (leaveId, employeeId, value) => {
//     setLeaveRequestInputs((prevInputs) => {
//       const parsed =
//         value === "" || value === null || value === undefined
//           ? 0
//           : parseFloat(value);
//       const newInputs = { ...prevInputs, [leaveId]: isNaN(parsed) ? 0 : parsed };

//       // sum only this employee's leave rows
//       const totalVacationHours = leaveRequests
//         .filter((l) => l.employee_id === employeeId)
//         .reduce((sum, l) => sum + (newInputs[l.id] ?? 0), 0);

//       const emp =
//         [...hourlyEmployees, ...salariedEmployees].find((e) => e.id === employeeId) ||
//         null;

//       if (emp) {
//         if (emp.type === "hourly") {
//           setHourlyVacationHours((prev) => ({
//             ...prev,
//             [employeeId]: totalVacationHours,
//           }));
//           handleInputChange(employeeId, "vacationHours", totalVacationHours);
//         } else {
//           setSalariedVacationHours((prev) => ({
//             ...prev,
//             [employeeId]: totalVacationHours,
//           }));
//           handleInputChange(employeeId, "vacationPay", totalVacationHours);
//         }
//       }
//       return newInputs;
//     });
//   };

//   // prefill overlay from HR inputs table
//   const applyPrefill = (prefill, hourly, salaried, pp, lr) => {
//     // 1) inputs
//     const merged = {};
//     [...hourly, ...salaried].forEach((emp) => {
//       const v = prefill?.inputsByEmployee?.[emp.id] || {};
//       merged[emp.id] = {
//         regularHours: v.regularHours ?? null,
//         overtimeHours: v.overtimeHours ?? null,
//         doubletimeHours: v.doubletimeHours ?? null,
//         cashAdvance: v.cashAdvance ?? null,
//         bonus: v.bonus ?? null,
//         tips: v.tips ?? null,
//         reimbursement: v.reimbursement ?? null,
//         other: v.other ?? null,
//         vacationHours: emp.type === "hourly" ? v.vacationHours ?? null : undefined,
//         vacationPay: emp.type === "salaried" ? v.vacationPay ?? null : undefined,
//       };
//     });
//     setEmployeeInputs(merged);

//     // 2) excluded set
//     const excluded = new Set();
//     Object.entries(prefill?.inputsByEmployee || {}).forEach(([empId, v]) => {
//       if (v?.is_excluded) excluded.add(Number(empId));
//     });
//     setExcludedEmployees(excluded);

//     // 3) leave allocations → per-leave inputs AND rollup hours boxes
//     const newLeaveInputs = {};
//     const hvh = {};
//     const svh = {};

//     // index helpful: payroll_inputs_id by employee
//     const piByEmp = {};
//     Object.entries(prefill?.inputsByEmployee || {}).forEach(([empId, v]) => {
//       if (v?.payroll_inputs_id) piByEmp[Number(empId)] = v.payroll_inputs_id;
//     });

//     lr.forEach((leave) => {
//       const pid = piByEmp[leave.employee_id];
//       if (!pid) return;
//       const arr = prefill?.leaveHoursByInputId?.[pid] || [];
//       const match = arr.find((x) => x.leave_request_id === leave.id);
//       if (match) {
//         const hrs = Number(match.hours_this_payroll || 0);
//         newLeaveInputs[leave.id] = hrs;
//       }
//     });

//     // compute totals per employee based on newLeaveInputs
//     [...hourly, ...salaried].forEach((emp) => {
//       const sum = lr
//         .filter((l) => l.employee_id === emp.id)
//         .reduce((acc, l) => acc + (newLeaveInputs[l.id] ?? 0), 0);
//       if (emp.type === "hourly") hvh[emp.id] = sum;
//       else svh[emp.id] = sum;
//     });

//     setLeaveRequestInputs(newLeaveInputs);
//     setHourlyVacationHours(hvh);
//     setSalariedVacationHours(svh);
//   };

//   useEffect(() => {
//     const load = async () => {
//       try {
//         const ppResp = await api.get(`staff/payroll/next/${businessId}`);
//         setPayPeriod(ppResp.data);

//         const [hourlyResp, salariedResp] = await Promise.all([
//           api.get(`/staff/businesses/${businessId}/employees/hourly`),
//           api.get(`/staff/businesses/${businessId}/employees/salary`),
//         ]);

//         const hourly = hourlyResp.data.map((e) => ({ ...e, type: "hourly" }));
//         const salaried = salariedResp.data.map((e) => ({ ...e, type: "salaried" }));
//         setHourlyEmployees(hourly);
//         setSalariedEmployees(salaried);

//         const lrResp = await api.get(
//           `/leave-requests/${businessId}/${formatDate(ppResp.data.start_date)}/${formatDate(ppResp.data.end_date)}`
//         );
//         setLeaveRequests(lrResp.data);

//         // pull prefill from new HR inputs table
//         const { data: prefill } = await api.get(
//           `/staff/payroll/inputs/${businessId}`,
//           {
//             params: {
//               start: formatDate(ppResp.data.start_date),
//               end: formatDate(ppResp.data.end_date),
//             },
//           }
//         );

//         applyPrefill(prefill, hourly, salaried, ppResp.data, lrResp.data);
//         setError(null);
//       } catch (err) {
//         console.error("StaffPayrollInputs load error:", err);
//         setError("Failed to load data. Please try again.");
//       } finally {
//         setLoading(false);
//       }
//     };
//     load();
//   }, [businessId]);


  




// function logAxiosError(tag, err) {
//   if (err?.response) {
//     console.error(`[${tag}]`, err.response.status, err.config?.url, err.response.data);
//   } else if (err?.request) {
//     console.error(`[${tag}] no response`, err.config?.url);
//   } else {
//     console.error(`[${tag}]`, err?.message || err);
//   }
// }

// useEffect(() => {
//   const load = async () => {
//     try {
//       const ppResp = await api
//         .get(`/payroll/next/${businessId}`)
//         .catch(e => { logAxiosError('GET /payroll/next', e); throw e; });
//       setPayPeriod(ppResp.data);

//       const [hourlyResp, salariedResp] = await Promise.all([
//         api.get(`/businesses/${businessId}/employees/hourly`).catch(e => { logAxiosError('GET /employees/hourly', e); throw e; }),
//         api.get(`/businesses/${businessId}/employees/salary`).catch(e => { logAxiosError('GET /employees/salary', e); throw e; }),
//       ]);

//       const hourly = hourlyResp.data.map(e => ({ ...e, type: 'hourly' }));
//       const salaried = salariedResp.data.map(e => ({ ...e, type: 'salaried' }));
//       setHourlyEmployees(hourly);
//       setSalariedEmployees(salaried);

//       const start = formatDate(ppResp.data.start_date);
//       const end   = formatDate(ppResp.data.end_date);

//       const lrResp = await api
//         .get(`/leave-requests/${businessId}/${start}/${end}`)
//         .catch(e => { logAxiosError('GET /leave-requests', e); throw e; });
//       setLeaveRequests(lrResp.data);

//       // ✅ Correct prefill call
//       const prefillResp = await api
//         .get('/biz-payroll-inputs', {
//           params: { business_id: businessId, start_date: start, end_date: end },
//         })
//         .catch(e => { logAxiosError('GET /biz-payroll-inputs', e); throw e; });

//       applyPrefill(prefillResp.data, hourly, salaried, ppResp.data, lrResp.data);
//       setError(null);
//     } catch {
//       setError('Failed to load HR inputs. Check console for the failing request.');
//     } finally {
//       setLoading(false);
//     }
//   };
//   load();
// }, [businessId]);



//   const handleSave = async () => {
//     // enforce: hourly regular hours required unless excluded
//     const missing = hourlyEmployees.filter((emp) => {
//       if (excludedEmployees.has(emp.id)) return false;
//       const rr = employeeInputs[emp.id]?.regularHours;
//       return rr === null || rr === undefined;
//     });
//     if (missing.length) {
//       alert(
//         `Regular Hours required for: ${missing
//           .map((m) => `${m.first_name} ${m.last_name}`)
//           .join(", ")}`
//       );
//       return;
//     }

    

//     const rows = [...hourlyEmployees, ...salariedEmployees].map((emp) => {
//       const v = employeeInputs[emp.id] || {};
//       return {
//         employee_id: emp.id,
//         is_excluded: excludedEmployees.has(emp.id),
//         regularHours: v.regularHours ?? null,
//         overtimeHours: v.overtimeHours ?? null,
//         doubletimeHours: v.doubletimeHours ?? null,
//         cashAdvance: v.cashAdvance ?? null,
//         bonus: v.bonus ?? null,
//         tips: v.tips ?? null,
//         reimbursement: v.reimbursement ?? null,
//         other: v.other ?? null,
//         vacationHours: emp.type === "hourly" ? v.vacationHours ?? null : null,
//         vacationPay: emp.type === "salaried" ? v.vacationPay ?? null : null,
//       };
//     });

//     const leaveRows = leaveRequests
//       .filter((l) => leaveRequestInputs[l.id] != null)
//       .map((l) => ({
//         employee_id: l.employee_id,
//         leave_request_id: l.id,
//         hours_this_payroll: Number(leaveRequestInputs[l.id] || 0),
//       }));

//     try {
//       await api.post(`/staff/payroll/inputs/${businessId}/upsert`, {
//         start_date: formatDate(payPeriod.start_date),
//         end_date: formatDate(payPeriod.end_date),
//         pay_date: formatDate(payPeriod.pay_date),
//         frequency: payPeriod.frequency,
//         rows,
//         leaveRows,
//       });
//       alert("Saved HR inputs.");
//     } catch (err) {
//       console.error("Save HR inputs error:", err);
//       alert("Failed to save HR inputs.");
//     }
//   };

//   if (loading) return <p>Loading employees...</p>;
//   if (error) return <p>{error}</p>;

//   return (
//     <div className="run-new-main">
//       <h4 className="run-new-page-head">Payroll (HR Inputs)</h4>

//       {payPeriod && (
//         <div className="pay-period-payroll-cont">
//           <div className="ppl-pay">
//             <label className="pay-period-label">Pay Period:</label>{" "}
//             <span className="pay-period-content">
//               {new Date(payPeriod.start_date).toLocaleDateString()} -{" "}
//               {new Date(payPeriod.end_date).toLocaleDateString()}
//               <br />
//             </span>
//           </div>

//           <div className="ppl-pay">
//             <label className="pay-period-label">Pay Date:</label>{" "}
//             <span className="pay-period-content">
//               {new Date(payPeriod.pay_date).toLocaleDateString()}
//             </span>
//           </div>
//         </div>
//       )}

//       <div className="run-new-employee-list">
//         {[...hourlyEmployees, ...salariedEmployees].map((employee) => {
//           const employeeLeaveRequests = leaveRequests.filter(
//             (leave) => leave.employee_id === employee.id
//           );
//           const hasLeaveRequests = employeeLeaveRequests.length > 0;

//           return (
//             <div
//               key={employee.id}
//               className={`run-new-employee-block ${
//                 hasLeaveRequests ? "employee-leave-highlight" : ""
//               }`}
//             >
//               {/* optional leave summary + requests (same layout) */}
//               {hasLeaveRequests && (
//                 <>
//                   <div className="vacation-summary">
//                     <div className="summary-div">Vacation Summary:</div>{" "}
//                     {employee.total_vacation_hours_earned ? (
//                       <>
//                         Earned:{" "}
//                         <div>{employee.total_vacation_hours_earned ?? 0} hrs</div> |{" "}
//                         Used: <div>{employee.total_vacation_hours_used ?? 0} hrs</div> |{" "}
//                         Available:{" "}
//                         <div>{employee.available_vacation_hours ?? 0} hrs</div>
//                       </>
//                     ) : (
//                       "No vacation hours logged yet."
//                     )}
//                   </div>

//                   <div className="leave-requests-section">
//                     <h4>Leave Request:</h4>
//                     <div className="leave-request-header">
//                       <span className="span-type">Type</span>
//                       <span className="date-span">Dates</span>
//                       <span className="days-req">Total Days</span>
//                       <span className="hours-req">Hours Req</span>
//                       <span className="hours-paid">Hours Paid</span>
//                       <span className="days-paid">Days Paid</span>
//                       <span className="hours-left">Hours Left</span>
//                       <span className="days-left">Days Left</span>
//                       <span className="period-days">Days in Period</span>
//                       <span className="this-span">Hrs This Payroll</span>
//                     </div>

//                     {employeeLeaveRequests.map((leave) => {
//                       const totalHoursRequested =
//                         Number(leave.total_hours_requested) || 0;
//                       const totalHoursPaid = Number(leave.total_hours_paid) || 0;
//                       const totalDaysRequested =
//                         Number(leave.total_days_requested) || 0;
//                       const totalDaysPaid = Number(leave.total_days_paid) || 0;
//                       const leaveDaysInPeriod =
//                         Number(leave.leave_days_in_period) || 0;

//                       return (
//                         <div key={leave.id} className="leave-request-row">
//                           <span className="span-type">{leave.request_type}</span>
//                           <div className="date-span">
//                             {new Date(leave.start_date).toLocaleDateString()} -{" "}
//                             {new Date(leave.end_date).toLocaleDateString()}
//                           </div>
//                           <span className="days-req">{totalDaysRequested}</span>
//                           <span className="hours-req">{totalHoursRequested}</span>
//                           <span className="hours-paid">{totalHoursPaid}</span>
//                           <span className="days-paid">{totalDaysPaid}</span>
//                           <span className="hours-left">
//                             {totalHoursRequested - totalHoursPaid}
//                           </span>
//                           <span className="days-left">
//                             {totalDaysRequested - totalDaysPaid}
//                           </span>
//                           <span className="period-days">{leaveDaysInPeriod}</span>
//                           <input
//                             type="number"
//                             placeholder="Vac. Hours"
//                             value={leaveRequestInputs[leave.id] ?? ""}
//                             onChange={(e) =>
//                               handleLeaveInputChange(
//                                 leave.id,
//                                 leave.employee_id,
//                                 e.target.value
//                               )
//                             }
//                           />
//                         </div>
//                       );
//                     })}
//                   </div>
//                 </>
//               )}

//               {/* Row 1: Basic Info (same classes) */}
//               <div className="employee-info-row">
//                 <div className="skip-it">
//                   <label className="skip-label"> Skip </label>
//                   <input
//                     type="checkbox"
//                     className="run-new-checkbox"
//                     checked={excludedEmployees.has(employee.id)}
//                     onChange={() => handleCheckboxChange(employee.id)}
//                   />
//                 </div>

//                 <span className="run-new-name">
//                   {employee.first_name} {employee.last_name}
//                 </span>
//                 <span className="run-new-type">
//                   {employee.type === "hourly" ? "Hourly" : "Salary"}
//                 </span>
//                 <span className="run-new-pay">
//                   {employee.type === "hourly"
//                     ? `$${employee.hourly_amt} /hr`
//                     : `$${getPayPeriodSalary(employee.salary_amt)} /period`}
//                 </span>
//                 <span
//                   className="toggle-arrow"
//                   onClick={() => toggleInputs(employee.id)}
//                 >
//                   {expandedRows[employee.id] ? "▲" : "▼"}
//                 </span>
//               </div>

//               {/* Row 2: Visible Inputs (same classes/fields) */}
//               <div className="employee-inputs-row">
//                 {employee.type === "hourly" ? (
//                   <>
//                     <input
//                       type="number"
//                       placeholder="Reg. Hours"
//                       value={employeeInputs[employee.id]?.regularHours ?? ""}
//                       onChange={(e) =>
//                         handleInputChange(
//                           employee.id,
//                           "regularHours",
//                           e.target.value
//                         )
//                       }
//                     />
//                     <input
//                       type="number"
//                       placeholder="OT Hours"
//                       value={employeeInputs[employee.id]?.overtimeHours ?? ""}
//                       onChange={(e) =>
//                         handleInputChange(
//                           employee.id,
//                           "overtimeHours",
//                           e.target.value
//                         )
//                       }
//                     />
//                     <input
//                       type="number"
//                       placeholder="DT Hours"
//                       value={employeeInputs[employee.id]?.doubletimeHours ?? ""}
//                       onChange={(e) =>
//                         handleInputChange(
//                           employee.id,
//                           "doubletimeHours",
//                           e.target.value
//                         )
//                       }
//                     />
//                     <input
//                       type="number"
//                       placeholder="Vac. Hours"
//                       value={
//                         hourlyVacationHours[employee.id] ??
//                         employeeInputs[employee.id]?.vacationHours ??
//                         ""
//                       }
//                       onChange={(e) => {
//                         const value = e.target.value;
//                         setHourlyVacationHours((prev) => ({
//                           ...prev,
//                           [employee.id]:
//                             value === "" ? "" : parseFloat(value) || 0,
//                         }));
//                         handleInputChange(employee.id, "vacationHours", value);
//                       }}
//                     />
//                   </>
//                 ) : (
//                   <input
//                     type="number"
//                     placeholder="Vac. Hours"
//                     value={
//                       salariedVacationHours[employee.id] ??
//                       employeeInputs[employee.id]?.vacationPay ??
//                       ""
//                     }
//                     onChange={(e) => {
//                       const value = e.target.value;
//                       setSalariedVacationHours((prev) => ({
//                         ...prev,
//                         [employee.id]:
//                           value === "" ? "" : parseFloat(value) || 0,
//                       }));
//                       handleInputChange(employee.id, "vacationPay", value);
//                     }}
//                   />
//                 )}
//               </div>

//               {/* Row 3: Expanded Inputs (same classes/fields) */}
//               {expandedRows[employee.id] && (
//                 <>
//                   <div className="vacation-summary-expanded">
//                     <div className="summary-div">Vacation Summary:</div>{" "}
//                     {employee.total_vacation_hours_earned ? (
//                       <>
//                         Earned:{" "}
//                         <div>{employee.total_vacation_hours_earned ?? 0} hrs</div> |{" "}
//                         Used: <div>{employee.total_vacation_hours_used ?? 0} hrs</div> |{" "}
//                         Available:{" "}
//                         <div>{employee.available_vacation_hours ?? 0} hrs</div>
//                       </>
//                     ) : (
//                       "No vacation hours logged yet."
//                     )}
//                   </div>

//                   <div className="employee-extra-inputs">
//                     <input
//                       type="number"
//                       placeholder="Cash Advance"
//                       value={employeeInputs[employee.id]?.cashAdvance ?? ""}
//                       onChange={(e) =>
//                         handleInputChange(
//                           employee.id,
//                           "cashAdvance",
//                           e.target.value
//                         )
//                       }
//                     />
//                     <input
//                       type="number"
//                       placeholder="Bonus"
//                       value={employeeInputs[employee.id]?.bonus ?? ""}
//                       onChange={(e) =>
//                         handleInputChange(employee.id, "bonus", e.target.value)
//                       }
//                     />
//                     <input
//                       type="number"
//                       placeholder="Tips"
//                       value={employeeInputs[employee.id]?.tips ?? ""}
//                       onChange={(e) =>
//                         handleInputChange(employee.id, "tips", e.target.value)
//                       }
//                     />
//                     <input
//                       type="number"
//                       placeholder="Reimbursement"
//                       value={employeeInputs[employee.id]?.reimbursement ?? ""}
//                       onChange={(e) =>
//                         handleInputChange(
//                           employee.id,
//                           "reimbursement",
//                           e.target.value
//                         )
//                       }
//                     />
//                     <input
//                       type="number"
//                       placeholder="Other"
//                       value={employeeInputs[employee.id]?.other ?? ""}
//                       onChange={(e) =>
//                         handleInputChange(employee.id, "other", e.target.value)
//                       }
//                     />
//                   </div>
//                 </>
//               )}
//             </div>
//           );
//         })}
//       </div>

//       {/* Use the same submit button class for consistent styling */}
//       <button className="run-new-submit" onClick={handleSave}>
//         Save
//       </button>
//     </div>
//   );
// };

// export default StaffPayrollInputsPage;
